import { useEffect, useRef } from 'react';
import { isObject } from '../domain/projection';
import type { ScenarioEntry, SourceValue } from '../domain/types';
/** Readable lossless source fields, including nested Unknown metadata and ordered arrays. */
export function SourceFields({ value }: { readonly value: SourceValue }) {
  if (value === null) return <span>Source null</span>;
  if (typeof value !== 'object') return <span>{String(value)}</span>;
  if (Array.isArray(value))
    return value.length ? (
      <ol>
        {value.map((v, i) => (
          <li key={i}>
            <SourceFields value={v} />
          </li>
        ))}
      </ol>
    ) : (
      <span>Source empty array — no declaration; no absence or safety conclusion</span>
    );
  if (isObject(value))
    return (
      <dl className="scc-fields">
        {Object.entries(value).map(([key, v]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd>
              <SourceFields value={v} />
            </dd>
          </div>
        ))}
      </dl>
    );
  return null;
}
export function ScenarioEntryInspector({
  entry,
  onClose,
}: {
  readonly entry: ScenarioEntry;
  readonly onClose: () => void;
}) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    close.current?.focus();
  }, [entry]);
  return (
    <section
      className="scc-inspector"
      role="dialog"
      aria-modal="false"
      aria-labelledby="scc-entry-title"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }
      }}
    >
      <button ref={close} onClick={onClose}>
        关闭详情
      </button>
      <h2 id="scc-entry-title">
        {entry.code} · {entry.name}
      </h2>
      <p>源位置 {entry.sourcePath}</p>
      <SourceFields value={entry.source} />
      {entry.related.length > 0 && (
        <>
          <h3>源声明的关联（不是可用性匹配）</h3>
          <SourceFields value={entry.related} />
        </>
      )}
      {entry.unknowns.length > 0 && (
        <>
          <h3>Unknown · 需确认事项</h3>
          <p>相应声明 Owner 需确认原因、影响与缺失事实；占位角色不授予任何人权限。</p>
          {entry.unknowns.map((u) => (
            <div key={u.path}>
              <p>{entry.sourcePath + u.path}</p>
              <SourceFields value={u.value} />
            </div>
          ))}
        </>
      )}
    </section>
  );
}
